import { useState, useEffect } from 'react'
import { getOAuthProviders, initiateOAuth } from '../api/client'
import { LoadingSpinner } from './LoadingSpinner'
import type { OAuthProvider } from '../types'
import { providerLabel, rememberOAuthFlow } from './oauthFlow'

interface OAuthProvidersProps {
  mode: 'sign_in' | 'sign_up'
  onError: (message: string) => void
  inviteCode?: string
}

export function OAuthProviders({ mode, onError, inviteCode }: OAuthProvidersProps) {
  const [providers, setProviders] = useState<OAuthProvider[]>([])
  const [loading, setLoading] = useState<string | null>(null)

  useEffect(() => {
    getOAuthProviders()
      .then((data) => {
        const enabled = data.providers.filter((p: OAuthProvider) => p.enabled)
        if (!enabled.find((p: OAuthProvider) => p.name === 'google')) {
          enabled.push({ name: 'google', enabled: false })
        }
        setProviders(enabled)
      })
      .catch(() => {
        setProviders([{ name: 'google', enabled: false }])
      })
  }, [])

  const handleOAuth = async (provider: string) => {
    onError('')
    setLoading(provider)
    try {
      // Store invite code in sessionStorage so OAuthCallback can include it in the exchange
      if (inviteCode) {
        sessionStorage.setItem('preview_invite_code', inviteCode)
      } else {
        sessionStorage.removeItem('preview_invite_code')
      }
      const { auth_url } = await initiateOAuth(provider)
      // OAuthCallback reads this back to exchange the code with the right provider
      rememberOAuthFlow(provider)
      window.location.href = auth_url
    } catch (err) {
      onError(err instanceof Error ? err.message : `Failed to start ${providerLabel(provider)} login`)
      setLoading(null)
    }
  }

  if (providers.length === 0) return null

  const actionLabel = mode === 'sign_up' ? 'Sign up with' : 'Continue with'
  const dividerLabel = mode === 'sign_up' ? 'Or sign up with email' : 'Or continue with email'

  return (
    <div className="space-y-3">
      {providers.map((provider) => {
        const isDisabled = !provider.enabled
        const isGitHub = provider.name === 'github'

        return (
          <button
            key={provider.name}
            onClick={() => !isDisabled && handleOAuth(provider.name)}
            disabled={isDisabled || loading !== null}
            className={
              isGitHub
                ? 'group relative w-full flex items-center justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gray-900 hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                : 'group relative w-full flex items-center justify-center py-2.5 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
            }
          >
            {loading === provider.name ? (
              <LoadingSpinner size="sm" className={`-ml-1 mr-3 ${isGitHub ? 'text-white' : 'text-gray-500'}`} />
            ) : (
              <ProviderIcon name={provider.name} dark={isGitHub} />
            )}
            {loading === provider.name
              ? `Connecting to ${providerLabel(provider.name)}...`
              : isDisabled
                ? `${providerLabel(provider.name)} (Coming soon)`
                : `${actionLabel} ${providerLabel(provider.name)}`}
          </button>
        )
      })}

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-gray-50 text-gray-500">{dividerLabel}</span>
        </div>
      </div>
    </div>
  )
}

function ProviderIcon({ name, dark }: { name: string; dark?: boolean }) {
  if (name === 'github') {
    return (
      <svg className={`w-5 h-5 mr-3 ${dark ? 'text-white' : ''}`} fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.6.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
      </svg>
    )
  }
  if (name === 'google') {
    return (
      <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
    )
  }
  return (
    <svg className="w-5 h-5 mr-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  )
}
